import _ from "lodash";
import {
  CANVAS_IDS,
  KONVA_ATTR,
  KONVA_EVENTS,
  KONVA_ITEM_TYPE,
} from "../Konva/KonvaConstants";
import {
  EVALUATION_MODE_CONFIG,
  LABEL_CONFIG,
  TEXT_ELEMENT_CONFIG,
  TRANFORMER_CONFIG,
} from "../Konva/KonvaConfig";
import {
  isTextNode,
  isImageNode,
  isLabelNode,
  getLabelAnchorPayload,
  onTransformerAttachedToLabel,
  onTransformerDetachedToLabel,
} from "../Konva/KonvaElements";
import { listenBackEvents, getCanvasRelativeSize } from "../KonvaCommonUtils";
import {
  maintainTransformationScale,
  updateTransformerHandler,
} from "../Konva/KonvaUtils";
import { generateRandomId } from "Utils";

const cachedArrowImage = { circled: null, outline: null };
const cachedTickImage = {};
let cachedGrayArrow = null;

/**
 *
 * @param {*} props
 * @description add events for labelling question create mode
 */
const addTeacherCreateModeEventListener = props => {
  const { konva, stage, layer, transformer } = props;

  let currentActiveAnchor = null;
  let highlightArrow = null;
  let arrowTransformerPoint = null;
  let currentSelectedArrow = null;
  let draggableArrow = null;

  // * Change cursor on mouse hover
  const onMouseOver = e => {
    const node = e.target;
    const isShape = node instanceof konva.Shape;
    stage.container().style.cursor = isShape ? "pointer" : "default";
  };

  // * add transfomer on clicking shape
  const onClick = e => {
    const node = e.target;
    const name = node.name();

    const isArrowNode = _.includes(name, CANVAS_IDS.LABEL_ARROW);
    const isArrowTransformAnchor = _.includes(
      name,
      `#${CANVAS_IDS.ARROW_TRANSFORMER}`
    );
    if (isArrowTransformAnchor) return;

    if (currentSelectedArrow) {
      const arrow = stage.findOne(`#${currentSelectedArrow}`);
      if (arrow) arrow.stroke("black");
    }

    if (!isArrowNode && _.first(arrowTransformerPoint)) {
      arrowTransformerPoint[0].visible(false);
      arrowTransformerPoint[1].visible(false);
    }

    if (!isLabelAnchorNode(name)) {
      // * Add transformer to text and image
      updateTransformerHandler(node, props);
    } else if (isLabelNode) {
      // * Add new arrow on clicking anchor
      addNewArrowOnThisLabel(node, props, { endPoint: false });
    }
  };

  // * fire ondrop event when item is dropped
  const onDragEnd = e => {
    const node = e.target;
    const name = node.name();

    if (
      _.includes(name, `#${CANVAS_IDS.ARROW_TRANSFORMER}`) &&
      currentSelectedArrow
    ) {
      /**
       * On Arrow Transfrom End
       */
      const arrowId = currentSelectedArrow;
      const arrow = stage.findOne(`#${arrowId}`);
      const labelId = _.get(_.split(arrow.name(), "#"), 1);
      const arrows = stage.getAttr(`${labelId}#${KONVA_ATTR.LABEL_ARROWS}`);
      const payload = {
        id: labelId,
        arrows: _.map(arrows, arrow => {
          if (_.get(arrow, "id") === arrowId) {
            return { ...arrow, to: [node.x(), node.y()] };
          }
          return arrow;
        }),
      };
      if (_.isFunction(props?.onDragEnd)) props.onDragEnd(payload);
      return;
    } else if (isLabelAnchorNode(name)) {
      if (draggableArrow) {
        addNewArrowOnThisLabel(node, props, { endPoint: true });
        draggableArrow.destroy();
        draggableArrow = null;
      }
    }

    const payload = { id: node.id(), x: node.x(), y: node.y() };
    node.moveTo(layer);
    if (node instanceof konva.Shape) props.onDragEnd(payload);
  };

  // * maintain shape scale on transform
  const onTransform = e => {
    const node = e.target;
    if (!node) return;

    maintainTransformationScale(node, props);
  };

  // * give a callback on transform end with updated shape props
  const onTransformEnd = e => {
    const node = e.target;

    const isText = isTextNode(node, konva);
    const isImage = isImageNode(node, konva);

    const payload = {
      id: node.id(),
      x: node.x(),
      y: node.y(),
    };

    if (isImage) {
      _.set(payload, "width", node.width());
      _.set(payload, "height", node.height());
    } else if (isText) {
      _.set(payload, "fontSize", node.fontSize());
    }

    props.onTransform(payload);
  };

  // * on attaching transformer with elements
  const onTransformerAttached = e => {
    transformer.visible(true);
    const node = e.evt;
    if (!node) return;

    const isArrowNode = node instanceof konva.Arrow;
    currentSelectedArrow = null;

    if (isLabelNode(node)) {
      transformer.visible(false);
      onTransformerAttachedToLabel(node, stage);
    } else if (isArrowNode) {
      transformer.visible(false);
      currentSelectedArrow = node.id();
      arrowTransformerPoint = onTransformerAttachedToArrow(
        node,
        props,
        arrowTransformerPoint
      );
      onTransformerDetachedToLabel(stage);
    } else {
      transformer.visible(true);
      onTransformerDetachedToLabel(stage);
    }
  };

  // * on detaching transformer with elements
  const onTransformerDetached = e => {
    transformer.visible(true);
    onTransformerDetachedToLabel(stage, e?.evt, transformer);
  };

  // * on Mouse move over Anchor, make it highlighted
  const onMouseMove = e => {
    const node = e.target;
    const name = _.first(_.split(node.name(), "#"));

    const isLabelAnchor = _.some(
      [
        CANVAS_IDS.LABEL_ANCHOR.TOP,
        CANVAS_IDS.LABEL_ANCHOR.RIGHT,
        CANVAS_IDS.LABEL_ANCHOR.BOTTOM,
        CANVAS_IDS.LABEL_ANCHOR.LEFT,
      ],
      anchor => _.includes(name, anchor)
    );

    if (isLabelAnchor) {
      const nodeId = node.id();
      if (nodeId === currentActiveAnchor) return;
      currentActiveAnchor = nodeId;
      node.fill("transparent");
      node.strokeWidth(0);
      node.radius(getCanvasRelativeSize(10));
      highlightArrow = showHighlightArrow(node, highlightArrow, props);
      // * add arrow on hover effect
    } else {
      if (currentActiveAnchor) {
        const anchorNode = stage.findOne(`#${currentActiveAnchor}`);
        if (anchorNode) {
          anchorNode.radius(getCanvasRelativeSize(4));
          anchorNode.fill(TRANFORMER_CONFIG.ANCHORFILL);
          anchorNode.strokeWidth(getCanvasRelativeSize(1));
        }
        currentActiveAnchor = null;
      }
      if (highlightArrow) {
        highlightArrow.visible(false);
        cachedGrayArrow.visible(false);
      }
    }
  };

  // * on pointer dragged, update arrow position
  const onDragMove = e => {
    const node = e.target;
    const name = node.name();
    if (
      _.includes(name, `#${CANVAS_IDS.ARROW_TRANSFORMER}`) &&
      currentSelectedArrow
    ) {
      const arrowId = currentSelectedArrow;
      const arrow = stage.findOne(`#${arrowId}`);
      const points = arrow.points();
      const newPoints = [points[0], points[1], node.x(), node.y()];
      arrow.points(newPoints);

      const supportArrow = stage.findOne(`#${arrowId}-dash-support`);
      supportArrow.points(newPoints);
    } else if (
      _.includes(name, `${KONVA_ITEM_TYPE.LABEL}#${KONVA_ITEM_TYPE.LABEL}`) &&
      _.first(arrowTransformerPoint)
    ) {
      _.first(arrowTransformerPoint).visible(false);
    } else if (isLabelAnchorNode(name)) {
      if (highlightArrow) highlightArrow.visible(false);
      if (cachedGrayArrow) cachedGrayArrow.visible(false);

      if (draggableArrow) {
        const currentPoints = draggableArrow.points();
        draggableArrow.points([
          currentPoints[0],
          currentPoints[1],
          node.x(),
          node.y(),
        ]);
      }
    }
  };

  const onDragStart = e => {
    const node = e.target;
    const name = node.name();
    if (isLabelAnchorNode(name)) {
      const x = node.x();
      const y = node.y();
      const strokeWidth = getCanvasRelativeSize(
        LABEL_CONFIG.ARROW_STROKE_WIDTH
      );
      draggableArrow = new konva.Arrow({
        pointerLength: 8,
        pointerWidth: 8,
        fill: "black",
        stroke: "black",
        strokeWidth: strokeWidth,
        dash: [strokeWidth, strokeWidth],
        points: [x, y, x, y],
      });
      layer.add(draggableArrow);
    } else if (isLabelNode(node)) {
      // * Resetting anchor to original size on dragging label node
      if (currentActiveAnchor) {
        const anchorNode = stage.findOne(`#${currentActiveAnchor}`);
        if (anchorNode) {
          anchorNode.radius(getCanvasRelativeSize(4));
          anchorNode.fill(TRANFORMER_CONFIG.ANCHORFILL);
          anchorNode.strokeWidth(getCanvasRelativeSize(1));
        }
        currentActiveAnchor = null;
      }
      // * Hiding anchor highlighter on dragging label node
      if (highlightArrow) {
        highlightArrow.visible(false);
        cachedGrayArrow.visible(false);
      }
    }
  };

  // * listening konva canvas events
  listenBackEvents(stage, transformer, {
    onClick,
    onDragEnd,
    onDragStart,
    onDragMove,
    onMouseOver,
    onMouseMove,
    onTransform,
    onTransformEnd,
    onTransformerAttached,
    onTransformerDetached,
  });

  return {};
};

/**
 *
 * @param {*} props
 * @description add events for labelling question edit mode
 */
const addEditAndPreviewModeEventListener = props => {
  const { stage, transformer } = props;

  let selectedLabel = null;

  // * Change cursor on mouse hover label
  const onMouseOver = e => {
    const node = e.target;
    const name = _.split(node.name(), "#");
    const isLabel = _.includes(name, KONVA_ITEM_TYPE.LABEL);
    stage.container().style.cursor = isLabel ? "pointer" : "default";
  };

  // * on clicking label add response input field
  const onClick = e => {
    const node = e.target;
    const name = _.split(node.name(), "#");
    const isLabel = _.includes(name, KONVA_ITEM_TYPE.LABEL);

    const currentEditId = stage.getAttr(KONVA_ATTR.IS_CHANGING_TEXT);
    if (currentEditId) {
      const activeElement = document.activeElement;

      /**
       * Issue:
       *  * To save current selected label content before going to next label node
       *  * On iPads (and mobile devices), interactions are primarily touch-based, &
       *  * browsers might not trigger blur in the same way as on desktop devices, which use mouse events.
       * Fix:
       *  * Manually trigger blur when interacting with the canvas
       *  * if click on canvas and active node element have open editor
       */
      if (
        _.isFunction(_.get(activeElement, "blur")) &&
        _.includes(_.get(activeElement, "id"), currentEditId)
      ) {
        activeElement.blur();
        return;
      } else {
        stage.setAttr(KONVA_ATTR.IS_CHANGING_TEXT, undefined);
      }
    }

    if (selectedLabel) {
      const labelContainer = stage.findOne(
        `#${selectedLabel}_${CANVAS_IDS.TEXT_WRAPPER_ID}`
      );
      if (labelContainer) {
        labelContainer.stroke(TEXT_ELEMENT_CONFIG.WRAPPER_STROKE);
      }
      selectedLabel = null;
    }

    if (isLabel) {
      const id = node.id();
      selectedLabel = id;

      const labelContainer = stage.findOne(
        `#${id}_${CANVAS_IDS.TEXT_WRAPPER_ID}`
      );
      if (labelContainer) {
        labelContainer.stroke(TRANFORMER_CONFIG.STROKE);
        stage.fire(KONVA_EVENTS.ATTACHINPUT, { target: node }, true);
      }
    }
  };

  // * listening konva canvas events
  listenBackEvents(stage, transformer, {
    onClick,
    onMouseOver,
  });
};

/**
 *
 * @param {*} props
 * View mode event listener to show ellipses text
 */
const addViewModeEventListener = props => {
  const { stage, transformer } = props;

  let selectedLabel = null;

  // * Change cursor on mouse hover label
  const onMouseOver = e => {
    const node = e.target;
    const name = _.split(node.name(), "#");
    const isLabel = _.includes(name, KONVA_ITEM_TYPE.LABEL);
    stage.container().style.cursor =
      isLabel && node.ellipsis() ? "pointer" : "default";
  };

  // * on clicking label, if it is ellipse make it full text readable
  const onClick = e => {
    const node = e.target;
    const name = _.split(node.name(), "#");
    const isLabel = _.includes(name, KONVA_ITEM_TYPE.LABEL);

    if (selectedLabel) {
      // if already selected, and ellipsis, change width to default
      const selectedNode = stage.findOne(`#${selectedLabel}`);
      const labelContainer = stage.findOne(
        `#${selectedLabel}_${CANVAS_IDS.TEXT_WRAPPER_ID}`
      );
      if (selectedNode && labelContainer) {
        labelContainer.width(getCanvasRelativeSize(150, false, false));
        selectedNode.width(getCanvasRelativeSize(150, false, false));

        const tickNode = stage.findOne(`#${selectedLabel}_TICK`);
        if (tickNode) {
          tickNode.x(
            selectedNode.x() +
              selectedNode.width() -
              getCanvasRelativeSize(16) / 2
          );
        }
      }
      selectedLabel = null;
    }

    if (isLabel) {
      // if label node, and ellipsis, extend width to max-content
      const id = node.id();
      selectedLabel = id;

      const labelContainer = stage.findOne(
        `#${id}_${CANVAS_IDS.TEXT_WRAPPER_ID}`
      );
      if (labelContainer) {
        node.width(null);
        node.width(
          Math.max(node.width(), getCanvasRelativeSize(150, false, false))
        );
        labelContainer.width(node.width());

        const tickNode = stage.findOne(`#${id}_TICK`);
        if (tickNode) {
          tickNode.x(node.x() + node.width() - getCanvasRelativeSize(16) / 2);
        }
      }
    }
  };

  // * listening konva canvas events
  listenBackEvents(stage, transformer, {
    onClick,
    onMouseOver,
  });
};

/**
 *
 * @param {*} konvaInstance
 * @param {*} mode
 * @param {*} cb
 * @description Event listener for drag and drop question
 */
export const addLabellingQuestionEventListener = (
  konvaInstance = {},
  mode = "",
  cb = () => {}
) => {
  if (mode === "create") {
    addTeacherCreateModeEventListener(konvaInstance);
  } else if (mode === "edit" || mode === "preview") {
    addEditAndPreviewModeEventListener(konvaInstance);
  } else if (mode === "view" || mode === "solution_view") {
    addViewModeEventListener(konvaInstance);
  }
  if (_.isFunction(cb)) cb();
};

/**
 *
 * @param {*} node
 * @param {*} highlightArrow
 * @param {*} props
 * @returns Arrow highlighted image
 */
const showHighlightArrow = (node, highlightArrow, { konva, virtualLayer }) => {
  if (!node) return;
  const imageSize = getCanvasRelativeSize(20);
  const offsetSize = imageSize / 2;

  const grayArrowSizeX = getCanvasRelativeSize(49);
  const grayArrowSizeY = getCanvasRelativeSize(16);

  if (!highlightArrow) {
    // * adding highlighted gray arrow
    if (cachedGrayArrow) cachedGrayArrow.destroy();
    cachedGrayArrow = new konva.Image({
      id: "label-anchor-highlighted-gray-forward-arrow",
      width: grayArrowSizeX,
      height: grayArrowSizeY,
      listening: false,
      draggable: false,
    });
    virtualLayer.add(cachedGrayArrow);

    if (cachedArrowImage.outline) {
      cachedGrayArrow.image(cachedArrowImage.outline);
    } else {
      const arrowGrayImage = new Image();
      arrowGrayImage.src = LABEL_CONFIG.OUTLINE_GRAY_ARROW_SRC;
      arrowGrayImage.onload = function () {
        cachedGrayArrow.image(this);
        cachedArrowImage.outline = this;
      };
    }

    // * Adding new image node for highlight arrow if not exist
    highlightArrow = new konva.Image({
      id: "label-anchor-highlight-arrow",
      width: imageSize,
      height: imageSize,
      listening: false,
      draggable: false,
    });
    virtualLayer.add(highlightArrow);

    if (cachedArrowImage.circled) {
      // * Using cached image else fetching new
      highlightArrow.image(cachedArrowImage.circled);
    } else {
      const arrowImage = new Image();
      arrowImage.src = LABEL_CONFIG.FILLED_ARROW_SRC;
      arrowImage.onload = function () {
        highlightArrow.image(this);
        cachedArrowImage.circled = this;
      };
    }
  }

  // * Updating arrow image element attrs based on achor highlighted
  const name = node.name();
  highlightArrow.visible(true);
  cachedGrayArrow.visible(true);

  highlightArrow.x(node.x() - offsetSize);
  highlightArrow.y(node.y() - offsetSize);
  cachedGrayArrow.x(node.x());
  cachedGrayArrow.y(node.y());

  if (_.includes(name, CANVAS_IDS.LABEL_ANCHOR.TOP)) {
    highlightArrow.rotation(270);
    highlightArrow.y(node.y() + offsetSize);

    cachedGrayArrow.rotation(270);
    cachedGrayArrow.y(node.y() - grayArrowSizeY);
    cachedGrayArrow.x(node.x() - grayArrowSizeY / 2);
  } else if (_.includes(name, CANVAS_IDS.LABEL_ANCHOR.BOTTOM)) {
    highlightArrow.rotation(90);
    highlightArrow.x(node.x() + offsetSize);

    cachedGrayArrow.rotation(90);
    cachedGrayArrow.x(node.x() + grayArrowSizeY / 2);
  } else if (_.includes(name, CANVAS_IDS.LABEL_ANCHOR.LEFT)) {
    highlightArrow.rotation(180);
    highlightArrow.x(node.x() + offsetSize);
    highlightArrow.y(node.y() + offsetSize);

    cachedGrayArrow.rotation(180);
    cachedGrayArrow.x(node.x());
    cachedGrayArrow.y(node.y() + grayArrowSizeY / 2);
  } else if (_.includes(name, CANVAS_IDS.LABEL_ANCHOR.RIGHT)) {
    highlightArrow.rotation(0);

    cachedGrayArrow.rotation(0);
    cachedGrayArrow.y(node.y() - grayArrowSizeY / 2);
  }

  return highlightArrow;
};

/**
 *
 * @param {*} param
 * @returns Arrow initial points
 */
const getArrowInitialPoints = ([dx, dy]) => {
  const canvasSize = getCanvasRelativeSize(false, true);
  const x = Math.min(Math.max(dx, 3), _.get(canvasSize, "width", dx) - 3);
  const y = Math.min(Math.max(dy, 3), _.get(canvasSize, "height", dy) - 3);
  return [x, y];
};

/**
 *
 * @param {*} node
 * @param {*} props
 * @returns Adding new arrows on clicking anchor with 75 as default length
 */
const addNewArrowOnThisLabel = (node, props, { endPoint }) => {
  if (!node || !_.isFunction(_.get(props, "onDragEnd"))) return;

  let labelId = node.id();
  _.forEach(getLabelAnchorPayload(""), item => {
    labelId = _.replace(labelId, _.get(item, "id"), "");
  });

  const currentArrows =
    props?.stage?.getAttr(`${labelId}#${KONVA_ATTR.LABEL_ARROWS}`) || [];

  const id = generateRandomId();
  const to = [node.x(), node.y()];
  const from = _.last(_.split(node.name(), "_"));

  if (!endPoint) {
    const arrowLength = getCanvasRelativeSize(75);
    if (from === "TOP") {
      to[1] = to[1] - arrowLength;
    } else if (from === "LEFT") {
      to[0] = to[0] - arrowLength;
    } else if (from === "RIGHT") {
      to[0] = to[0] + arrowLength;
    } else if (from === "BOTTOM") {
      to[1] = to[1] + arrowLength;
    }
  }

  const arrowPayload = {
    id: labelId,
    arrows: [...currentArrows, { id, from, to: getArrowInitialPoints(to) }],
  };

  props.onDragEnd(arrowPayload);
};

/**
 *
 * @param {*} node
 * @param {*} params
 * @param {*} arrowTransformerPoint
 * @returns Adding start and end pointer of transformer for arrow
 */
const onTransformerAttachedToArrow = (
  node,
  { stage, virtualLayer, konva },
  arrowTransformerPoint
) => {
  if (!node) return;
  const points = node.points();
  let startPoint = _.first(arrowTransformerPoint);
  let endPoint = _.last(arrowTransformerPoint);

  if (!endPoint) {
    const commonPayload = {
      radius: getCanvasRelativeSize(6),
      strokeWidth: getCanvasRelativeSize(1),
      stroke: TRANFORMER_CONFIG.STROKE,
      fill: TRANFORMER_CONFIG.ANCHORFILL,
      visible: false,
    };
    startPoint = new konva.Circle({
      id: generateRandomId(),
      x: points[0],
      y: points[1],
      listening: false,
      draggable: false,
      ...commonPayload,
    });
    endPoint = new konva.Circle({
      id: generateRandomId(),
      name: `${node.id()}#${CANVAS_IDS.ARROW_TRANSFORMER}`,
      x: points[2],
      y: points[3],
      listening: true,
      draggable: true,
      ...commonPayload,
      dragBoundFunc: pos => {
        pos.x = Math.min(Math.max(pos.x, 1), stage.width());
        pos.y = Math.min(Math.max(pos.y, 1), stage.height());
      },
    });
    virtualLayer.add(endPoint);
    virtualLayer.add(startPoint);
  }

  endPoint.visible(true);
  startPoint.visible(true);
  startPoint.x(points[0]);
  startPoint.y(points[1]);
  endPoint.x(points[2]);
  endPoint.y(points[3]);
  node.stroke(TRANFORMER_CONFIG.STROKE);

  return [startPoint, endPoint];
};

/**
 *
 * @param {*} mode
 * @param {*} params
 */
export const showLabellingEvaluationView = (
  mode,
  items,
  { konva, stage, layer, options = {} }
) => {
  if (
    mode !== "view" ||
    !_.get(options, "showViewSolutionButton") ||
    _.get(options, "viewSolution") ||
    !_.get(options, "isSubmitted", false)
  ) {
    // * Not showing evaluation, when viewing solution or mode is not evaluation
    return;
  }

  _.forEach(items, item => {
    if (_.get(item, "category") === KONVA_ITEM_TYPE.LABEL) {
      const isCorrect = _.get(item, "isCorrect", false);

      const id = `${_.get(item, "id")}_${CANVAS_IDS.TEXT_WRAPPER_ID}`;
      const element = stage.findOne(`#${id}`);
      if (element) {
        element.stroke(
          !isCorrect
            ? EVALUATION_MODE_CONFIG.WRONG_ANSWER_STROKE
            : EVALUATION_MODE_CONFIG.CORRECT_ANSWER_STROKE
        );

        element.strokeWidth(
          getCanvasRelativeSize(EVALUATION_MODE_CONFIG.TEXT_STROKE_WIDTH)
        );

        const tickPosition = {
          x: element.x() + element.width(),
          y: element.y(),
        };

        const imageUrl = isCorrect
          ? EVALUATION_MODE_CONFIG.CORRECT_ANSWER_SRC
          : EVALUATION_MODE_CONFIG.WRONG_ANSWER_SRC;

        const iconSize = getCanvasRelativeSize(16);
        const imageNode = new konva.Image({
          id: `${_.get(item, "id")}_TICK`,
          x: tickPosition.x - iconSize / 2,
          y: tickPosition.y - iconSize / 2,
          width: iconSize,
          height: iconSize,
          draggable: false,
          listening: false,
        });
        layer.add(imageNode);

        // * Using cached image if available
        if (cachedTickImage[imageUrl]) {
          imageNode.image(cachedTickImage[imageUrl]);
          return;
        }

        const img = new Image();
        img.src = imageUrl;
        img.onload = function () {
          cachedTickImage[imageUrl] = this;
          imageNode.image(this);
        };
      }
    }
  });
};

/**
 *
 * @param {*} name
 * @returns identify is this anchor node of label
 */
const isLabelAnchorNode = name => {
  return (
    _.includes(name, CANVAS_IDS.LABEL_ANCHOR.TOP) ||
    _.includes(name, CANVAS_IDS.LABEL_ANCHOR.RIGHT) ||
    _.includes(name, CANVAS_IDS.LABEL_ANCHOR.BOTTOM) ||
    _.includes(name, CANVAS_IDS.LABEL_ANCHOR.LEFT)
  );
};
